import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Package, User, FileText, Camera, 
  CheckCircle, AlertTriangle, Scan, Loader2, 
  Check, ChevronRight, X, Trash2, ClipboardCheck,
  Calendar, Euro, Building, Hash, Truck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { compressImage } from '../lib/imageCompress';

const API = `${window.location.origin}/api`;

const PACK_IMAGES = {
  'pack_dj_booth': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/gu1bc6jg_Pack%20%C3%A9cran%20LED%202m2.png',
  'pack_compact': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/vm87efqp_Pack%20%C3%A9cran%20LED%204m2.png',
  'pack_standard': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/nenk0vgh_Pack%20%C3%A9cran%20LED%207m2.png',
  'pack_xl': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/uncbzflz_Pack%20%C3%A9cran%20LED%2015m2.png',
  'default': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/nenk0vgh_Pack%20%C3%A9cran%20LED%207m2.png'
};

const getPackImageUrl = (packId, packName) => {
  const n = (packName || '').toLowerCase();
  if (n.includes('dj') || n.includes('2m') || n.includes('cabine')) return PACK_IMAGES['pack_dj_booth'];
  if (n.includes('compact') || n.includes('4m')) return PACK_IMAGES['pack_compact'];
  if (n.includes('standard') || n.includes('7m')) return PACK_IMAGES['pack_standard'];
  if (n.includes('xl') || n.includes('15m')) return PACK_IMAGES['pack_xl'];
  return PACK_IMAGES.default;
};

const InspectionProcessingPage = () => {
  const { reservationId } = useParams();
  const [searchParams] = useSearchParams();
  const inspectionType = searchParams.get('type') || 'departure';
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [assignedItems, setAssignedItems] = useState([]);
  
  // Mode: 'verify' (has assigned items) or 'assign' (no items, departure)
  const [mode, setMode] = useState('verify');
  const [packComponents, setPackComponents] = useState([]);
  
  // Steps depend on mode
  // assign mode: 1=Scan/Assign, 2=Inspection, 3=Expédition
  // verify mode: 1=Scan/Verify, 2=Inspection, 3=Validation
  const [currentStep, setCurrentStep] = useState(1);
  
  // Scanning state
  const [scanInput, setScanInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const scanInputRef = useRef(null);
  
  // For verify mode: scannedItems = { SERIAL: { ...item, scanned_at } }
  const [scannedItems, setScannedItems] = useState({});
  
  // For assign mode: scannedByComponent = { product_id: [{ serial, product_name, stock_item_id }] }
  const [scannedByComponent, setScannedByComponent] = useState({});
  
  // Inspection state
  const [itemConditions, setItemConditions] = useState({});
  const [itemPhotos, setItemPhotos] = useState({});
  const [inspectionNotes, setInspectionNotes] = useState('');
  
  // Shipping state (assign mode only)
  const [shippingInfo, setShippingInfo] = useState({ carrier: 'DASHER', trackingNumber: '' });
  
  // Focus lock
  useEffect(() => {
    let intervalId = null;
    if (currentStep === 1) {
      scanInputRef.current?.focus();
      intervalId = setInterval(() => {
        if (scanInputRef.current && document.activeElement !== scanInputRef.current && !scanInputRef.current.disabled) {
          scanInputRef.current.focus();
        }
      }, 150);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [currentStep, scannedItems, scannedByComponent]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/orders/${reservationId}/details`, { withCredentials: true });
        const res = response.data.reservation;
        setReservation(res);
        const items = res?.assigned_items || [];
        setAssignedItems(items);
        
        const isDeparture = inspectionType === 'departure';
        const hasItems = items.length > 0;
        
        if (isDeparture && !hasItems) {
          // Assign mode: load pack components
          setMode('assign');
          const comps = response.data.pack_components || [];
          setPackComponents(comps);
          const initial = {};
          comps.forEach(c => { initial[c.product_id] = []; });
          setScannedByComponent(initial);
        } else if (!isDeparture) {
          // Return mode: build components from assigned items for component grid
          setMode('return');
          const comps = response.data.pack_components || [];
          
          // Group assigned items by product_id to know exactly what to expect back
          const grouped = {};
          items.forEach(item => {
            const pid = item.product_id || 'unknown';
            if (!grouped[pid]) {
              grouped[pid] = {
                product_id: pid,
                product_name: item.product_name || 'Article inconnu',
                quantity: 0,
                serials: [],
                is_alternative: false,
                alternative_group: null
              };
              // Enrich with alternative info from pack components
              const matchComp = comps.find(c => c.product_id === pid);
              if (matchComp) {
                grouped[pid].alternative_group = matchComp.alternative_group || null;
                grouped[pid].is_alternative = matchComp.is_alternative || false;
              }
            }
            grouped[pid].quantity++;
            grouped[pid].serials.push(item.serial_number);
          });
          
          const returnComps = Object.values(grouped);
          setPackComponents(returnComps);
          const initial = {};
          returnComps.forEach(c => { initial[c.product_id] = []; });
          setScannedByComponent(initial);
          
          // Init conditions for all items
          const conditions = {};
          items.forEach(item => {
            conditions[item.serial_number] = { condition: 'excellent', notes: '', is_functional: true };
          });
          setItemConditions(conditions);
        } else {
          // Verify mode (departure with pre-assigned items)
          setMode('verify');
          const conditions = {};
          items.forEach(item => {
            conditions[item.serial_number] = { condition: 'excellent', notes: '', is_functional: true };
          });
          setItemConditions(conditions);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reservationId, inspectionType]);
  
  // === VERIFY MODE HANDLERS ===
  const handleVerifyScan = (serialNumber) => {
    const serial = serialNumber.trim().toUpperCase();
    if (!serial) return;
    const item = assignedItems.find(i => i.serial_number?.toUpperCase() === serial);
    if (!item) { toast.error(`Article ${serial} non trouvé dans cette commande`); setScanInput(''); return; }
    if (scannedItems[serial]) { toast.warning(`Article ${serial} déjà scanné`); setScanInput(''); return; }
    setScannedItems(prev => ({ ...prev, [serial]: { ...item, scanned_at: new Date().toISOString() } }));
    toast.success(`Article ${serial} scanné`);
    setScanInput('');
    if (Object.keys(scannedItems).length + 1 === assignedItems.length) {
      toast.success('Tous les articles ont été scannés !');
    }
  };
  
  // === ASSIGN MODE HANDLERS ===
  const handleAssignScan = async (serialNumber) => {
    const serial = serialNumber.trim().toUpperCase();
    if (!serial) return;
    setScanning(true);
    setScanInput('');
    try {
      const { data: stockItem } = await axios.get(`${API}/admin/stock-items/by-serial/${encodeURIComponent(serial)}`, { withCredentials: true });
      
      if (stockItem.status !== 'DISPONIBLE') {
        toast.error(`Article ${serial} n'est pas disponible (${stockItem.status})`);
        setScanning(false);
        return;
      }
      
      // Find matching component
      let targetComp = packComponents.find(c => c.product_id === stockItem.product_id);
      let targetProductId = targetComp?.product_id;
      
      // If not found directly, try alternative_group
      if (!targetComp) {
        targetComp = packComponents.find(c => c.alternative_group && c.alternative_group === stockItem.alternative_group);
        if (targetComp) targetProductId = targetComp.product_id;
      }
      
      if (!targetComp) {
        toast.error(`${stockItem.product_name} ne fait pas partie de ce pack`);
        setScanning(false);
        return;
      }
      
      // Check if already scanned
      const allScanned = Object.values(scannedByComponent).flat();
      if (allScanned.find(s => s.serial === serial)) {
        toast.warning(`Article ${serial} déjà scanné`);
        setScanning(false);
        return;
      }
      
      // Check capacity for this component
      const currentCount = scannedByComponent[targetProductId]?.length || 0;
      if (currentCount >= targetComp.quantity) {
        // Try alternatives
        const altComp = packComponents.find(c => 
          c.product_id !== targetProductId && 
          c.alternative_group === targetComp.alternative_group &&
          (scannedByComponent[c.product_id]?.length || 0) < c.quantity
        );
        if (altComp) {
          targetProductId = altComp.product_id;
          targetComp = altComp;
        } else {
          toast.error(`Quantité maximale atteinte pour ${targetComp.product_name}`);
          setScanning(false);
          return;
        }
      }
      
      // Assign to reservation via API
      await axios.post(`${API}/admin/orders/${reservationId}/assign-item`, {
        serial_number: serial,
        product_id: targetProductId
      }, { withCredentials: true });
      
      // Update local state
      setScannedByComponent(prev => ({
        ...prev,
        [targetProductId]: [...(prev[targetProductId] || []), {
          serial,
          product_name: stockItem.product_name,
          stock_item_id: stockItem.id,
          product_id: stockItem.product_id
        }]
      }));
      
      // Init condition for this item
      setItemConditions(prev => ({
        ...prev,
        [serial]: { condition: 'excellent', notes: '', is_functional: true }
      }));
      
      toast.success(`${stockItem.product_name} — ${serial}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors du scan';
      toast.error(msg);
    } finally {
      setScanning(false);
    }
  };
  
  // === RETURN MODE HANDLER ===
  const handleReturnScan = (serialNumber) => {
    const serial = serialNumber.trim().toUpperCase();
    if (!serial) return;
    
    // Check if this serial belongs to the reservation's assigned items
    const item = assignedItems.find(i => i.serial_number?.toUpperCase() === serial);
    if (!item) {
      toast.error(`Article ${serial} non trouvé dans cette commande`);
      setScanInput('');
      return;
    }
    
    // Check if already scanned
    const allScanned = Object.values(scannedByComponent).flat();
    if (allScanned.find(s => s.serial === serial)) {
      toast.warning(`Article ${serial} déjà scanné`);
      setScanInput('');
      return;
    }
    
    // Find matching component in packComponents by product_id
    let targetPid = item.product_id;
    let targetComp = packComponents.find(c => c.product_id === targetPid);
    
    // If not found directly, try alternative_group match
    if (!targetComp) {
      targetComp = packComponents.find(c => c.alternative_group && c.serials && c.serials.includes(serial));
      if (targetComp) targetPid = targetComp.product_id;
    }
    
    if (!targetComp) {
      toast.error(`Composant non trouvé pour ${serial}`);
      setScanInput('');
      return;
    }
    
    // Check capacity
    const currentCount = scannedByComponent[targetPid]?.length || 0;
    if (currentCount >= targetComp.quantity) {
      toast.warning(`Tous les ${targetComp.product_name} ont déjà été scannés`);
      setScanInput('');
      return;
    }
    
    // Add to scannedByComponent
    setScannedByComponent(prev => ({
      ...prev,
      [targetPid]: [...(prev[targetPid] || []), {
        serial,
        product_name: item.product_name || targetComp.product_name,
        product_id: item.product_id
      }]
    }));
    
    // Init condition if not set
    setItemConditions(prev => ({
      ...prev,
      [serial]: prev[serial] || { condition: 'excellent', notes: '', is_functional: true }
    }));
    
    toast.success(`${item.product_name || targetComp.product_name} — ${serial}`);
    setScanInput('');
    
    // Check if all done
    const totalScanned = allScanned.length + 1;
    const totalExpected = packComponents.reduce((sum, c) => sum + c.quantity, 0);
    if (totalScanned >= totalExpected) {
      toast.success('Tous les articles ont été scannés !');
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (mode === 'assign') handleAssignScan(scanInput);
      else if (mode === 'return') handleReturnScan(scanInput);
      else handleVerifyScan(scanInput);
    }
  };
  
  // Photo handling
  const handleItemPhotoUpload = async (serial, e) => {
    const files = Array.from(e.target.files);
    const current = itemPhotos[serial] || [];
    if (current.length + files.length > 5) { toast.error('Maximum 5 photos par article'); e.target.value = ''; return; }
    e.target.value = '';
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} trop volumineux (max 10 Mo)`); continue; }
      try {
        const data = await compressImage(file);
        setItemPhotos(prev => ({
          ...prev,
          [serial]: [...(prev[serial] || []), { id: Date.now() + Math.random(), name: file.name, data, file }]
        }));
      } catch {
        toast.error(`Impossible de charger ${file.name}`);
      }
    }
  };
  
  const removeItemPhoto = (serial, photoId) => {
    setItemPhotos(prev => ({ ...prev, [serial]: (prev[serial] || []).filter(p => p.id !== photoId) }));
  };
  
  const updateItemCondition = (serialNumber, field, value) => {
    setItemConditions(prev => ({ ...prev, [serialNumber]: { ...prev[serialNumber], [field]: value } }));
  };
  
  // Computed values
  const getProgress = () => {
    if (mode === 'verify') {
      const scannedCount = Object.keys(scannedItems).length;
      const total = assignedItems.length;
      return { scanned: scannedCount, total, allDone: scannedCount === total && total > 0 };
    } else if (mode === 'return') {
      // Return mode: count ALL components (no alt group dedup, exact quantities from assigned items)
      const allScanned = Object.values(scannedByComponent).flat();
      const totalRequired = packComponents.reduce((sum, c) => sum + c.quantity, 0);
      return { scanned: allScanned.length, total: totalRequired, allDone: allScanned.length >= totalRequired };
    } else {
      // Assign mode: dedup alternative groups
      const allScanned = Object.values(scannedByComponent).flat();
      
      let totalRequired = 0;
      const processedGroups = new Set();
      for (const c of packComponents) {
        if (c.alternative_group) {
          if (processedGroups.has(c.alternative_group)) continue;
          processedGroups.add(c.alternative_group);
          totalRequired += c.quantity;
        } else {
          totalRequired += c.quantity;
        }
      }
      return { scanned: allScanned.length, total: totalRequired, allDone: allScanned.length >= totalRequired };
    }
  };
  
  const progress = getProgress();
  
  // Get all scanned serials for inspection step
  const getAllScannedSerials = () => {
    if (mode === 'verify') {
      return Object.entries(scannedItems).map(([serial, item]) => ({ serial, product_name: item.product_name }));
    } else {
      // Both 'assign' and 'return' modes
      return Object.values(scannedByComponent).flat().map(s => ({ serial: s.serial, product_name: s.product_name }));
    }
  };
  
  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // 1. Create inspection
      const formData = new FormData();
      formData.append('reservation_id', reservationId);
      formData.append('type', inspectionType);
      formData.append('general_notes', inspectionNotes);
      
      const allSerials = getAllScannedSerials();
      const itemsData = allSerials.map(({ serial, product_name }) => ({
        serial_number: serial,
        product_name,
        condition: itemConditions[serial]?.condition || 'excellent',
        notes: itemConditions[serial]?.notes || '',
        is_functional: itemConditions[serial]?.is_functional !== false,
        photo_count: (itemPhotos[serial] || []).length
      }));
      formData.append('items', JSON.stringify(itemsData));
      
      Object.keys(itemPhotos).forEach(serial => {
        (itemPhotos[serial] || []).forEach(photo => {
          if (photo.file) formData.append(`photo_${serial}`, photo.file);
        });
      });
      
      await axios.post(`${API}/admin/inspections`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // 2. If assign mode (departure + new items), also process shipment
      if (mode === 'assign' && shippingInfo.trackingNumber.trim()) {
        await axios.post(`${API}/admin/orders/${reservationId}/process`, {
          carrier: shippingInfo.carrier,
          tracking_number: shippingInfo.trackingNumber,
          notes: inspectionNotes
        }, { withCredentials: true });
        toast.success('État des lieux créé et commande expédiée !');
      } else {
        toast.success(`État des lieux ${inspectionType === 'departure' ? 'de départ' : 'de retour'} créé !`);
      }
      
      navigate('/admin');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setSubmitting(false);
    }
  };
  
  const conditionOptions = [
    { value: 'excellent', label: 'Excellent', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'good', label: 'Bon', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'fair', label: 'Correct', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'damaged', label: 'Endommagé', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];
  
  const steps = mode === 'assign' 
    ? [
        { step: 1, label: 'Scan & Assignation', icon: Scan },
        { step: 2, label: 'État des articles', icon: ClipboardCheck },
        { step: 3, label: 'Expédition', icon: Truck }
      ]
    : mode === 'return'
    ? [
        { step: 1, label: 'Scan retour', icon: Scan },
        { step: 2, label: 'État des articles', icon: ClipboardCheck },
        { step: 3, label: 'Validation', icon: CheckCircle }
      ]
    : [
        { step: 1, label: 'Vérification', icon: Scan },
        { step: 2, label: 'État des articles', icon: ClipboardCheck },
        { step: 3, label: 'Validation', icon: CheckCircle }
      ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-[#DFFF00] animate-spin" />
          <p className="text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!reservation) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Commande non trouvée</h2>
          <Button onClick={() => navigate('/admin')} variant="outline">Retour au dashboard</Button>
        </div>
      </div>
    );
  }
  
  // Helper for assign mode: component progress
  const getComponentProgress = (comp) => {
    const scanned = scannedByComponent[comp.product_id]?.length || 0;
    // Return mode: each component has exact expected quantities, no autoComplete
    if (mode === 'return') {
      return { scanned, total: comp.quantity, autoCompleted: false };
    }
    if (comp.is_alternative && comp.alternative_group) {
      const groupMembers = packComponents.filter(g => g.alternative_group === comp.alternative_group);
      const isFirst = groupMembers[0]?.product_id === comp.product_id;
      if (!isFirst) {
        const otherScanned = groupMembers.filter(g => g.product_id !== comp.product_id)
          .reduce((s, g) => s + (scannedByComponent[g.product_id]?.length || 0), 0);
        if (otherScanned >= groupMembers[0].quantity) return { scanned, total: comp.quantity, autoCompleted: true };
      }
    }
    return { scanned, total: comp.quantity, autoCompleted: false };
  };
  
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />Retour
              </Button>
              <div className="h-6 w-px bg-slate-700" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#DFFF00]/20 flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-[#DFFF00]" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">
                    État des lieux {inspectionType === 'departure' ? 'de départ' : 'de retour'}
                  </h1>
                  <p className="text-sm text-slate-400">#{reservationId.slice(0, 8)} — {reservation.customer?.firstName} {reservation.customer?.lastName}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-4 py-2">
              <span className="text-sm text-slate-400">Progression</span>
              <span className={`text-lg font-bold ${progress.allDone ? 'text-green-400' : 'text-[#DFFF00]'}`}>
                {progress.scanned}/{progress.total}
              </span>
            </div>
          </div>
        </div>
      </header>
      
      {/* Steps */}
      <div className="bg-slate-800/30 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-center gap-4">
            {steps.map(({ step, label, icon: Icon }) => (
              <React.Fragment key={step}>
                {step > 1 && <ChevronRight className={`w-5 h-5 ${currentStep >= step ? 'text-[#DFFF00]' : 'text-slate-600'}`} />}
                <button
                  onClick={() => {
                    if (step === 1) setCurrentStep(1);
                    else if (step === 2 && progress.scanned > 0) setCurrentStep(2);
                    else if (step === 3 && progress.allDone) setCurrentStep(3);
                  }}
                  disabled={step > 1 && (step === 2 ? progress.scanned === 0 : !progress.allDone)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    currentStep === step ? 'bg-[#DFFF00]/20 text-[#DFFF00]'
                    : currentStep > step ? 'bg-green-500/20 text-green-400'
                    : 'bg-slate-800 text-slate-500'
                  }`}
                  data-testid={`step-${step}-btn`}
                >
                  {currentStep > step ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  <span className="text-sm font-medium">{label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
              <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2"><User className="w-4 h-4" />Client</h3>
              <div className="space-y-2">
                <p className="text-white font-medium">{reservation.customer?.firstName} {reservation.customer?.lastName}</p>
                {reservation.customer?.company && <p className="text-sm text-slate-400 flex items-center gap-2"><Building className="w-3 h-3" />{reservation.customer.company}</p>}
                <p className="text-sm text-slate-400">{reservation.customer?.email}</p>
                <p className="text-sm text-slate-400">{reservation.customer?.phone}</p>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-slate-700">
                <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2"><Package className="w-4 h-4" />Pack</h3>
              </div>
              <div className="p-3 flex items-center gap-3">
                <img src={getPackImageUrl(reservation.pack_id, reservation.pack_name)} alt={reservation.pack_name} className="w-16 h-16 object-cover rounded-lg" />
                <div>
                  <p className="text-white font-medium">{reservation.pack_name}</p>
                  <p className="text-sm text-slate-400">{reservation.pack_surface}</p>
                  <p className="text-[#DFFF00] font-semibold">{reservation.monthly_price}€/mois</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
              <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" />Infos commande</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Date</span><span className="text-white">{new Date(reservation.created_at).toLocaleDateString('fr-FR')}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Statut</span><span className="text-[#DFFF00]">{reservation.status}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Mode</span><span className="text-white">{mode === 'assign' ? 'Scan & Assignation' : mode === 'return' ? 'Scan retour' : 'Vérification'}</span></div>
              </div>
            </div>
          </div>
          
          {/* Main content */}
          <div className="lg:col-span-2 space-y-4">
            {/* STEP 1: SCAN */}
            {currentStep === 1 && (
              <>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Scan className="w-5 h-5 text-[#DFFF00]" />
                    {mode === 'assign' ? 'Scanner les articles du stock' : mode === 'return' ? 'Scanner les articles retournés' : 'Vérifier les articles assignés'}
                  </h3>
                  <div className="relative mb-6">
                    <Input
                      ref={scanInputRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onBlur={(e) => {
                        if (currentStep !== 1) return;
                        const to = e.relatedTarget;
                        if (to && to.closest && to.closest('button, select, input, textarea, a, [role="button"]')) return;
                        setTimeout(() => scanInputRef.current?.focus(), 50);
                      }}
                      placeholder="Scanner ou saisir le N° de série..."
                      className="bg-slate-700 border-slate-600 text-white text-lg py-6 pl-12"
                      readOnly={scanning}
                      autoFocus
                      data-testid="scan-input"
                    />
                    <Scan className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    {scanning && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#DFFF00] animate-spin" />}
                  </div>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Progression</span>
                      <span className="text-[#DFFF00]">{progress.scanned}/{progress.total} articles</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className="bg-[#DFFF00] h-2 rounded-full transition-all duration-300" style={{ width: `${progress.total > 0 ? (progress.scanned / progress.total) * 100 : 0}%` }} />
                    </div>
                  </div>
                </div>
                
                {/* Items list */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl">
                  <div className="p-4 border-b border-slate-700">
                    <h3 className="font-medium text-white flex items-center gap-2">
                      <Hash className="w-4 h-4 text-[#DFFF00]" />
                      {mode === 'verify' ? 'Articles assignés' : 'Contenu du pack'}
                    </h3>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {mode === 'verify' ? (
                      assignedItems.map((item, index) => {
                        const isScanned = scannedItems[item.serial_number?.toUpperCase()];
                        return (
                          <div key={index} className={`p-4 border-b border-slate-700/50 last:border-0 flex items-center justify-between ${isScanned ? 'bg-green-500/10' : ''}`}>
                            <div>
                              <p className="text-white font-medium">{item.product_name}</p>
                              <p className="text-sm text-slate-400 font-mono">{item.serial_number}</p>
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isScanned ? 'bg-green-500 border-green-500' : 'border-slate-600'}`}>
                              {isScanned && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      packComponents.map((comp, index) => {
                        const cp = getComponentProgress(comp);
                        const scanned = scannedByComponent[comp.product_id] || [];
                        return (
                          <div key={index} className={`p-4 border-b border-slate-700/50 last:border-0 ${cp.autoCompleted ? 'opacity-50' : ''}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="text-white font-medium text-sm">{comp.product_name}</p>
                                {comp.is_alternative && <span className="text-xs text-orange-400">Alternatif</span>}
                              </div>
                              <span className={`text-sm font-bold ${cp.scanned >= cp.total || cp.autoCompleted ? 'text-green-400' : 'text-[#DFFF00]'}`}>
                                {cp.autoCompleted ? <Check className="w-4 h-4 inline" /> : `${cp.scanned}/${cp.total}`}
                              </span>
                            </div>
                            {!cp.autoCompleted && cp.total > 0 && (
                              <div className="w-full bg-slate-700 rounded-full h-1.5 mb-2">
                                <div className="bg-[#DFFF00] h-1.5 rounded-full transition-all" style={{ width: `${(cp.scanned / cp.total) * 100}%` }} />
                              </div>
                            )}
                            {scanned.length > 0 && (
                              <div className="space-y-1 mt-2">
                                {scanned.map(s => (
                                  <div key={s.serial} className="flex items-center gap-2 text-xs text-green-400">
                                    <Check className="w-3 h-3" />
                                    <span className="font-mono">{s.serial}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                
                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={progress.scanned === 0}
                  className="w-full bg-[#DFFF00] text-black hover:bg-[#c8e600] py-6"
                  data-testid="continue-step2-btn"
                >
                  Continuer vers l'état des articles
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </>
            )}
            
            {/* STEP 2: INSPECTION */}
            {currentStep === 2 && (
              <>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-[#DFFF00]" />
                    État de chaque article
                  </h3>
                  <div className="space-y-4">
                    {getAllScannedSerials().map(({ serial, product_name }) => {
                      const condition = itemConditions[serial] || { condition: 'excellent', notes: '' };
                      const photos = itemPhotos[serial] || [];
                      return (
                        <div key={serial} className="bg-slate-700/50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="text-white font-medium">{product_name}</p>
                              <p className="text-sm text-slate-400 font-mono">{serial}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {conditionOptions.map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => updateItemCondition(serial, 'condition', opt.value)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                                  condition.condition === opt.value ? opt.color : 'bg-slate-800 text-slate-400 border-slate-600 hover:border-slate-500'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          <Input
                            placeholder="Remarque (optionnel)..."
                            value={condition.notes || ''}
                            onChange={(e) => updateItemCondition(serial, 'notes', e.target.value)}
                            className="bg-slate-800 border-slate-600 text-white text-sm mb-3"
                          />
                          <div>
                            <p className="text-xs text-slate-400 mb-2 flex items-center gap-1"><Camera className="w-3 h-3" />Photos ({photos.length}/5)</p>
                            <div className="flex flex-wrap gap-2">
                              {photos.map(photo => (
                                <div key={photo.id} className="relative group">
                                  <img src={photo.data} alt={photo.name} className="w-16 h-16 object-cover rounded-lg border border-slate-600" />
                                  <button onClick={() => removeItemPhoto(serial, photo.id)} className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X className="w-3 h-3 text-white" />
                                  </button>
                                </div>
                              ))}
                              {photos.length < 5 && (
                                <label className="flex flex-col items-center justify-center w-16 h-16 border border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-[#DFFF00] transition-colors">
                                  <Camera className="w-4 h-4 text-slate-500" />
                                  <input type="file" accept="image/*" multiple onChange={(e) => handleItemPhotoUpload(serial, e)} className="hidden" />
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-4">
                  <Button onClick={() => setCurrentStep(1)} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">
                    <ArrowLeft className="w-4 h-4 mr-2" />Retour
                  </Button>
                  <Button onClick={() => setCurrentStep(3)} className="flex-1 bg-[#DFFF00] text-black hover:bg-[#c8e600]" data-testid="continue-step3-btn">
                    {mode === 'assign' ? "Continuer vers l'expédition" : 'Continuer vers la validation'}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </>
            )}
            
            {/* STEP 3: SHIPPING (assign mode) or VALIDATION (verify mode) */}
            {currentStep === 3 && (
              <>
                {mode === 'assign' && (
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-4">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Truck className="w-5 h-5 text-[#DFFF00]" />
                      Informations d'expédition
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Transporteur</label>
                        <select
                          value={shippingInfo.carrier}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, carrier: e.target.value }))}
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded-xl px-4 py-3"
                          data-testid="carrier-select"
                        >
                          <option value="DASHER">DASHER</option>
                          <option value="DHL">DHL</option>
                          <option value="UPS">UPS</option>
                          <option value="FedEx">FedEx / TNT</option>
                          <option value="DPD">DPD</option>
                          <option value="Colissimo">Colissimo</option>
                          <option value="Autre">Autre</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Numéro de suivi *</label>
                        <Input
                          value={shippingInfo.trackingNumber}
                          onChange={(e) => setShippingInfo(prev => ({ ...prev, trackingNumber: e.target.value }))}
                          placeholder="N° de suivi..."
                          className="bg-slate-700 border-slate-600 text-white py-3"
                          data-testid="tracking-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#DFFF00]" />
                    Récapitulatif
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <span className="text-green-400">Articles scannés</span>
                      <span className="text-green-400 font-bold">{progress.scanned}/{progress.total}</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Photos ajoutées</span>
                      <span className="text-white font-bold">{Object.values(itemPhotos).reduce((sum, arr) => sum + arr.length, 0)}</span>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-slate-300 mb-2">État des articles :</p>
                      <div className="flex flex-wrap gap-2">
                        {conditionOptions.map(opt => {
                          const count = Object.values(itemConditions).filter(c => c.condition === opt.value).length;
                          if (count === 0) return null;
                          return <span key={opt.value} className={`px-2 py-1 rounded text-xs ${opt.color}`}>{opt.label}: {count}</span>;
                        })}
                      </div>
                    </div>
                    
                    {/* Notes */}
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Notes générales (optionnel)</label>
                      <textarea
                        value={inspectionNotes}
                        onChange={(e) => setInspectionNotes(e.target.value)}
                        placeholder="Remarques générales..."
                        rows={3}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-xl px-4 py-3 resize-none focus:border-[#DFFF00] focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <Button onClick={() => setCurrentStep(2)} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">
                    <ArrowLeft className="w-4 h-4 mr-2" />Retour
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || (mode === 'assign' && !shippingInfo.trackingNumber.trim())}
                    className="flex-1 bg-green-500 text-white hover:bg-green-600"
                    data-testid="validate-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : mode === 'assign' ? (
                      <Truck className="w-5 h-5 mr-2" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    {mode === 'assign' ? "Valider l'état des lieux et expédier" : "Valider l'état des lieux"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionProcessingPage;
